import React from "react";
import { XCircle } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { SimpleTableMobileView } from "./SimpleTableMobileView";
import { SimpleTableErrorBoundary } from "./ErrorBoundaries";
import type { BaseEntity, SimpleTableProps } from "./types";
import type { SimpleTableSetupResult } from "./SimpleTableSetup";

/**
 * Render conditional UI states: loading, error, and mobile view.
 * This function handles early returns for non-desktop views.
 *
 * NOTE: For loading state on desktop, we return null to let the table render
 * with the data it has. The table will show a loading indicator overlay instead
 * of being completely replaced with a spinner.
 */
export function renderSimpleTableConditional<TData extends BaseEntity>(
  setup: SimpleTableSetupResult<TData>,
  props: SimpleTableProps<TData>,
  isLoading: boolean,
  error: Error | null
): React.ReactNode {
  const { isMobile, hasMounted, config } = setup;

  // SSR / first-paint gate: the server cannot know the viewport, so until the
  // media query resolves on the client we render a neutral skeleton instead of
  // committing to the desktop table (which would then swap to mobile cards and
  // cause layout shift). Once mounted, isMobile is trustworthy.
  //
  // CRITICAL: only gate on mount BEFORE we have rows. The mount-skeleton must
  // never replace a table that already holds data — doing so left the page
  // showing the header count but an empty body until a manual refresh forced a
  // re-render. If rows are already in hand, render them; a one-frame
  // mobile/desktop flash is strictly better than a blank table.
  const hasRows = Array.isArray(props.data) && props.data.length > 0;
  if (!hasMounted && !hasRows) {
    return (
      <div
        className="flex-1 min-h-0 w-full animate-pulse rounded-lg border border-border bg-muted/30"
        aria-hidden="true"
        data-testid="crm-table-mount-skeleton"
      />
    );
  }

  // Error state - show error message
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <XCircle className="h-12 w-12 mx-auto mb-2" />
          <h3 className="text-lg font-medium">Error loading data</h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400">{error.message}</p>
      </div>
    );
  }

  // Mobile view - use existing mobile components
  // CRITICAL FIX: This early return now happens AFTER all hooks are called
  if (isMobile) {
    return (
      <SimpleTableErrorBoundary entityName={config.displayName}>
        <SimpleTableMobileView
          {...{
            config: props.config,
            data: props.data,
            isLoading: props.isLoading,
            error: props.error,
            onEdit: props.onEdit,
            onView: props.onView,
            onInlineEdit: props.onInlineEdit
              ? (payload: Record<string, any>) => props.onInlineEdit!(payload as TData)
              : undefined,
            onDelete: props.onDelete,
            onCreate: props.onCreate,
            onBulkAction: props.onBulkAction,
            savedViewsControls: props.savedViewsControls,
            mobileHeaderActions: props.mobileHeaderActions,
            onSavedViewsReady: props.onSavedViewsReady,
            loadMore: props.loadMore,
            hasNextPage: props.hasNextPage,
            isFetchingNextPage: props.isFetchingNextPage,
            // New mobile features
            savedViews: props.savedViews,
            onApplySavedView: props.onApplySavedView,
            onSaveView: props.onSaveView,
            onDeleteSavedView: props.onDeleteSavedView,
            onExport: props.onExport,
            currentPage: setup.state.effectiveCurrentPage,
            totalPages: props.totalPages,
            pageSize: setup.state.effectivePageSize,
            totalItems: props.totalItems,
            onPageChange: setup.state.effectiveSetCurrentPage,
            onPageSizeChange: setup.state.effectiveSetPageSize,
            onOpenDetailModal: props.onOpenDetailModal,
            visibleColumnIds: props.visibleColumnIds,
            hooks: props.hooks,
          }}
        />
      </SimpleTableErrorBoundary>
    );
  }

  // Desktop view: Return null to let the table render
  // The table will show existing data with a loading indicator overlay
  // This prevents the entire table from disappearing during search/filter updates
  return null;
}
